import { format, isSameDay, parseISO } from "date-fns";
import pt from "date-fns/locale/pt";
import type {
	LogbookNoteItem,
	LogbookSessionItem,
	LogbookPrescriptionItem,
	LogbookOrderItem,
	LogbookTaskTimelineItem,
	LogbookCustomerSurveyItem,
	LogbookEntry,
} from "../types";

export const buildTimelineEntries = (
	sessions: Array<LogbookSessionItem>,
	notes: Array<LogbookNoteItem>,
	prescriptions: Array<LogbookPrescriptionItem>,
	orders: Array<LogbookOrderItem>,
	tasks: Array<LogbookTaskTimelineItem>,
	customerSurveys: Array<LogbookCustomerSurveyItem>
): Array<LogbookEntry> => {
	const sortedSessions = [...sessions].sort(
		(a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
	);

	const linkedNotes = notes.filter((note) => note.sessions);
	const unlinkedNotes = notes.filter((note) => !note.sessions);

	const noteEntries: Array<LogbookEntry> = unlinkedNotes.map((note) => ({ type: "note", note }));
	const sessionEntries = makeSessionsEntries(sortedSessions, linkedNotes);
	const prescriptionEntries: Array<LogbookEntry> = prescriptions.map((prescription) => ({
		type: "prescription",
		prescription,
	}));
	const orderEntries = makeOrderEntries(orders);
	const taskEntries: Array<LogbookEntry> = tasks.map((task) => ({ type: "task", task }));
	const customerSurveyEntries: Array<LogbookEntry> = customerSurveys.map((customerSurvey) => ({ type: "customer_survey", customerSurvey }));

	const timeline = [...sessionEntries, ...noteEntries, ...prescriptionEntries, ...orderEntries, ...taskEntries, ...customerSurveyEntries].sort(
		(a, b) => getEntryDate(b).getTime() - getEntryDate(a).getTime()
	);

	return timeline;
};

const makeOrderEntries = (orders: Array<LogbookOrderItem>): Array<LogbookEntry> => {
	return orders.flatMap((order) => {
		const orderDate = order.order_date ? parseISO(order.order_date) : null;
		const paymentDate = order.payment_date ? parseISO(order.payment_date) : null;

		if (orderDate && paymentDate && isSameDay(orderDate, paymentDate)) {
			return [{ type: "order" as const, order, orderSubtype: "created_and_paid" as const, entryDate: order.order_date }];
		}

		const entries: Array<LogbookEntry> = [];
		if (orderDate) entries.push({ type: "order", order, orderSubtype: "created", entryDate: order.order_date });
		if (paymentDate) entries.push({ type: "order", order, orderSubtype: "paid", entryDate: order.payment_date! });
		return entries;
	});
};

const makeSessionsEntries = (sessions: Array<LogbookSessionItem>, linkedNotes: Array<LogbookNoteItem>) => {
	const sessionEntries: Array<LogbookEntry> = sessions.map((session) => {
		const notes = linkedNotes
			.filter((note) => note.sessions === session.id)
			.sort((a, b) => new Date(b.date_created).getTime() - new Date(a.date_created).getTime());
		return { type: "session", session, notes };
	});
	return sessionEntries;
};

const getEntryData = (entry: LogbookEntry) => {
	if (entry.type === "session") return { id: entry.session.id, dateString: entry.session.start_date };
	if (entry.type === "note") return { id: entry.note.id, dateString: entry.note.date_created };
	if (entry.type === "order") return { id: entry.order.id, dateString: entry.entryDate };
	if (entry.type === "task") return { id: entry.task.id, dateString: entry.task.date_created };
	if (entry.type === "customer_survey") return { id: entry.customerSurvey.id, dateString: entry.customerSurvey.date_created };
	return { id: entry.prescription.id, dateString: entry.prescription.date_created };
};

export const getEntryDate = (entry: LogbookEntry): Date => new Date(getEntryData(entry).dateString);
export const getEntryId = (entry: LogbookEntry): number => getEntryData(entry).id;
export const getEntryMonth = (entry: LogbookEntry): string => format(parseISO(getEntryData(entry).dateString), "MMMM yyyy", { locale: pt });
